import { merge } from 'lodash';
import React, { useMemo, useCallback } from 'react';

import { AvailabilitySortFields, SortDirection, GetAvailabilitiesQueryVariables } from '@/graphql';
import { BaseTableProps } from '@boilerplate/types/entity';
import Entity from '@/entities/availability';
import EntityTable, {
  defaultPage,
  defaultPageSize,
  defaultSortOrder,
  getDefaultSortField,
} from '@boilerplate/components/entity/EntityTable/EntityTable';
import { canPerformAction, useAuthenticatedUserStore } from '@/stores/UserStore';
import { openConfirmationDialog } from '@/stores/ConfirmationDialogStore';


export const useAvailabilitiesTable = ({ queryOptions }: { queryOptions?: GetAvailabilitiesQueryVariables } = {}) => {
  const defaultVariableOptions = useMemo<GetAvailabilitiesQueryVariables>(
    () => 
      merge(
        {
          sorting: {
            direction: defaultSortOrder === 'desc' ? SortDirection.Desc : SortDirection.Asc,
            field: getDefaultSortField(Entity.table.schema) as AvailabilitySortFields,
          },
          paging: {
            page: defaultPage,
            pageSize: defaultPageSize,
          },
                  withUser: true,
          },
          queryOptions,
        ), 
    [queryOptions]
    );
  
  const {
    data: availabilityData,
    items = [],
    loading,
    refetch: originalRefetch,
    variables,
  } = Entity.model.useGetAll({
    fetchPolicy: 'cache-and-network',
    variables: defaultVariableOptions,
  });

  const [remove] = Entity.model.useDelete();

  const refetch = useCallback(
    (variableOptions?: Partial<GetAvailabilitiesQueryVariables >) => {
      return originalRefetch(merge({}, defaultVariableOptions, variableOptions));
    },
    [defaultVariableOptions, originalRefetch]
  );

  const allActions = useMemo(() => ({ loading, refetch, remove }), [loading, refetch, remove]);
  const data = useMemo(
    () =>
      availabilityData
        ? { totalCount: availabilityData.availabilities.totalCount, rows: items }
        : { totalCount: 0, rows: [] },
    [availabilityData, items]
  );

  return { data, actions: allActions, refetch, defaultVariableOptions, variables };
}

export type AvailabilitiesBaseTableProps = BaseTableProps<typeof Entity.model>;

export default function AvailabilitiesBaseTable(props: AvailabilitiesBaseTableProps) {
  const tableProps = useAvailabilitiesTable();
  const authentictedUser = useAuthenticatedUserStore();

  return (
    <EntityTable
      {...props}
      {...tableProps}
      Entity={Entity}
      user={authentictedUser}
      canPerformThisAction={canPerformAction}
      openConfirmationDialog={openConfirmationDialog}
    />
  );
}
